import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import expenseService from '../../services/ExpenseService.js';

const money = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const when = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const timeAgo = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return when(value);
};

function logEntries(expense) {
  if (Array.isArray(expense?.approval_log) && expense.approval_log.length) {
    return expense.approval_log;
  }
  return [];
}

function findEntry(expense, step, action) {
  return logEntries(expense).find((entry) => entry.step === step && (!action || entry.action === action));
}

const ExpenseTrackModal = ({ expenseId, user, onClose, onChanged }) => {
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getById(expenseId);
      setExpense(response.data || response);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load expense');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [expenseId]);

  const role = user?.role;
  const stage = expense?.approval_stage || 'pending_admin';
  const isBranch = expense?.expense_kind === 'branch' || expense?.source_type === 'branch_agent';
  const canAccountantAct = expense && isBranch && stage === 'pending_accountant' && expense.status === 'pending' && ['Accountant', 'Admin', 'System Admin'].includes(role);
  const canAdminAct = expense && !expense.is_indent && stage === 'pending_admin' && expense.status === 'pending' && ['Admin', 'System Admin'].includes(role);
  const canOwnerAct = expense && !isBranch && !expense.is_indent && stage === 'pending_owner' && expense.status === 'pending' && ['Owner', 'System Admin'].includes(role);

  const created = findEntry(expense, 'created');
  const accountantDecision = logEntries(expense).filter((entry) => entry.step === 'accountant').slice(-1)[0];
  const adminDecision = logEntries(expense).filter((entry) => entry.step === 'admin').slice(-1)[0];
  const ownerDecision = logEntries(expense).filter((entry) => entry.step === 'owner').slice(-1)[0];
  const settlement = adminDecision?.settlement;
  const raisedName = created?.by_name || expense?.created_by_name || expense?.created_by || '—';
  const raisedRole = created?.role || 'Accountant';
  const raisedAt = created?.at || expense?.created_at;

  const decide = async (action) => {
    if ((action === 'reject' || action === 'review') && !remarks.trim()) {
      Alert.error('Add a remark before you reject or send this back');
      return;
    }
    try {
      setBusy(true);
      await expenseService.approve(expenseId, remarks.trim(), action);
      Alert.success(
        action === 'reject'
          ? 'Expense rejected'
          : action === 'review'
            ? 'Sent back to the creator'
            : canAccountantAct
            ? 'Sent to the admin'
            : isBranch
              ? 'Settled against the agent amount'
              : stage === 'pending_admin'
                ? 'Sent to the owner'
                : 'Payment done',
      );
      setRemarks('');
      await load();
      onChanged?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not update this expense');
    } finally {
      setBusy(false);
    }
  };

  const remarkBox = (placeholder) => (
    <div className="mt-3 space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Remark
        <textarea
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          rows={3}
          placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => decide('approve')} loading={busy}>Approve</Button>
        {canAdminAct && (
          <Button type="button" variant="secondary" onClick={() => decide('review')} loading={busy}>Send back</Button>
        )}
        <Button type="button" variant="danger" onClick={() => decide('reject')} loading={busy}>Reject</Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Expense tracking</p>
            <h2 className="text-lg font-semibold text-gray-900 truncate">{expense?.expense_id || expenseId}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !expense ? (
          <p className="px-5 py-8 text-sm text-gray-500">Loading tracking…</p>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-2xl font-bold text-gray-900">{money(expense.amount)}</p>
              </div>
              <div className="text-sm text-right">
                <p className="font-medium text-gray-800">{expense.category_display || 'Expense'}</p>
                <p className="text-gray-500">{expense.location_hub_name || 'No hub'}</p>
              </div>
            </div>

            <ol className="space-y-4">
              <li className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">1 · Raised</p>
                <p className="mt-1 text-base font-semibold text-gray-900">{raisedName}</p>
                <p className="text-sm text-gray-600">
                  {raisedRole}
                  {raisedAt ? ` · ${timeAgo(raisedAt)}` : ''}
                  {raisedAt ? ` · ${when(raisedAt)}` : ''}
                </p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-gray-500">Related to</dt>
                    <dd className="font-medium text-gray-800">{expense.category_display || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Amount</dt>
                    <dd className="font-medium text-gray-800">{money(expense.amount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Hub</dt>
                    <dd className="font-medium text-gray-800">{expense.location_hub_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Vendor</dt>
                    <dd className="font-medium text-gray-800">{expense.vendor_name || '—'}</dd>
                  </div>
                </dl>
                {expense.description ? (
                  <p className="mt-2 text-sm text-gray-700">{expense.description}</p>
                ) : null}
              </li>

              {isBranch && (
                <li className={`rounded-xl border p-4 ${canAccountantAct ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100'}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">2 · Accountant</p>
                  {accountantDecision ? (
                    <>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {accountantDecision.by_name || accountantDecision.by || 'Accountant'} {accountantDecision.action === 'approved' ? 'approved' : accountantDecision.action === 'rejected' ? 'rejected' : 'sent this back'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {accountantDecision.role || 'Accountant'}
                        {accountantDecision.at ? ` · ${timeAgo(accountantDecision.at)} · ${when(accountantDecision.at)}` : ''}
                      </p>
                      {accountantDecision.remarks ? (
                        <p className="mt-2 text-sm text-gray-800 bg-white border border-gray-100 rounded-lg px-3 py-2">{accountantDecision.remarks}</p>
                      ) : null}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-gray-600">Waiting for the accountant to review.</p>
                  )}
                  {canAccountantAct ? remarkBox('Write why you are approving, rejecting, or sending this back') : null}
                </li>
              )}

              <li className={`rounded-xl border p-4 ${canAdminAct ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100'}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{isBranch ? '3 · Admin' : '2 · Admin'}</p>
                {adminDecision ? (
                  <>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {adminDecision.by_name || adminDecision.by || 'Admin'} {adminDecision.action === 'approved' ? 'approved' : adminDecision.action === 'rejected' ? 'rejected' : 'sent this back'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {adminDecision.role || 'Admin'}
                      {adminDecision.at ? ` · ${timeAgo(adminDecision.at)} · ${when(adminDecision.at)}` : ''}
                    </p>
                    {adminDecision.remarks ? (
                      <p className="mt-2 text-sm text-gray-800 bg-white border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-line">{adminDecision.remarks}</p>
                    ) : null}
                    {settlement ? (
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <dt className="text-gray-500">Amount to return before</dt>
                          <dd className="font-medium text-gray-800">{money(settlement.paid_before)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Expense minus</dt>
                          <dd className="font-medium text-gray-800">{money(settlement.expense_amount)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Amount to return now</dt>
                          <dd className="font-medium text-gray-800">{money(settlement.paid_after)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Company to pay agent</dt>
                          <dd className="font-medium text-gray-800">{money(settlement.company_payable)}</dd>
                        </div>
                      </dl>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-1 text-sm text-gray-600">
                    {isBranch && stage === 'pending_accountant'
                      ? 'The accountant reviews this before admin.'
                      : 'Waiting for admin to approve or reject.'}
                  </p>
                )}
                {canAdminAct ? remarkBox(isBranch
                  ? 'Write why you are approving or sending this back. Approval settles it against the agent amount.'
                  : 'Write why you are approving, rejecting, or sending this back') : null}
              </li>

              {!isBranch && (
              <li className={`rounded-xl border p-4 ${canOwnerAct ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100'}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">3 · Owner</p>
                {ownerDecision ? (
                  <>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {ownerDecision.by_name || ownerDecision.by || 'Owner'} {ownerDecision.action === 'approved' ? 'approved · payment done' : 'rejected'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {ownerDecision.role || 'Owner'}
                      {ownerDecision.at ? ` · ${timeAgo(ownerDecision.at)} · ${when(ownerDecision.at)}` : ''}
                    </p>
                    {ownerDecision.remarks ? (
                      <p className="mt-2 text-sm text-gray-800 bg-white border border-gray-100 rounded-lg px-3 py-2">{ownerDecision.remarks}</p>
                    ) : null}
                  </>
                ) : stage === 'pending_owner' ? (
                  <p className="mt-1 text-sm text-gray-600">Admin has approved. Waiting for the owner.</p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">This reaches the owner after admin approval.</p>
                )}
                {canOwnerAct ? remarkBox('Write why you are approving or rejecting. Approval marks the payment done.') : null}
              </li>
              )}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseTrackModal;
